import Vue from 'vue';
import VueCurrencyFilter from 'vue-currency-filter';
import currencyMap from '@/util/currency';

/**
 * Installs and configures currency filters for the application based on venue settings.
 *
 * This function sets up the following currency filters:
 * 1. 'currency' - A standard currency filter with the venue's currency symbol
 * 2. 'customCurrency' - A configurable currency filter that shows/hides the currency symbol
 *    based on the venue's showCurrencySymbol setting
 * 3. 'currencyWithSymbol' - (In storeDesigner mode) A filter that always shows the currency symbol
 * 4. 'currencyWithoutSymbol' - (In storeDesigner mode) A filter that never shows the currency symbol
 *
 * @param {Object} venue - The venue object containing currency configuration
 * @param {string} venue.currencyCode - The currency code (e.g., 'GBP', 'USD', 'EUR')
 * @param {boolean} venue.showCurrencySymbol - Whether to show the currency symbol in the customCurrency filter
 * @param {boolean} [isStoreDesigner=false] - Whether the application is running in storeDesigner mode
 *
 * @example
 * // Setup currency filters for a venue with GBP currency and visible currency symbol
 * installCurrencyFilter({ currencyCode: 'GBP', showCurrencySymbol: true });
 *
 * // Setup currency filters for storeDesigner mode
 * installCurrencyFilter({ currencyCode: 'GBP', showCurrencySymbol: true }, true);
 *
 * // Usage in templates
 * // {{ price | currency }} -> £10.00
 * // {{ price | customCurrency }} -> £10.00 or 10.00 depending on showCurrencySymbol
 */
export default function installCurrencyFilter(venue, isStoreDesigner = false) {
    const cfg = currencyMap(venue.currencyCode, venue.address?.country);

    const baseConfig = {
        ...cfg,
        symbolSpacing: false,
        fractionCount: 2
    };

    const filtersConfig = [
        {
            ...baseConfig,
            name: 'currency'
        },
        {
            ...baseConfig,
            name: 'customCurrency',
            symbol: venue.showCurrencySymbol ? cfg.symbol : '',
            avoidEmptyDecimals: venue.showCurrencySymbol ? undefined : ''
        }
    ];

    if (isStoreDesigner) {
        filtersConfig.push({
            ...baseConfig,
            name: 'currencyWithSymbol',
            symbol: cfg.symbol,
            avoidEmptyDecimals: undefined
        });

        filtersConfig.push({
            ...baseConfig,
            name: 'currencyWithoutSymbol',
            symbol: '',
            avoidEmptyDecimals: ''
        });

        // In storeDesigner mode, when showCurrencySymbol changes, we need to update the customCurrency filter
        const filterName = venue.showCurrencySymbol
            ? 'currencyWithSymbol'
            : 'currencyWithoutSymbol';

        Vue.use(VueCurrencyFilter, filtersConfig);

        const currencyFilterFunction = Vue.filter(filterName);

        if (currencyFilterFunction) {
            Vue.filter('customCurrency', v => currencyFilterFunction(v));
        }
    } else {
        Vue.use(VueCurrencyFilter, filtersConfig);
    }
}
